import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";
import "./site-header-drawer.css";
import "./wine-surface.css";
import "./store-commerce.css";
import "./storefront-polish.css";
import "./store-pdp.css";
import "./home-hero.css";
import "./store-forms.css";
import "./storefront-ui-polish.css";
import "./site-header-brand.css";
import "./storefront-luxury-chrome.css";
import "./components/brand-logo.css";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  NavLink,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import About from "./About";
import ExhibitionsPage from "./ExhibitionsPage";
import Contact from "./Contact";
import Shop from "./Shop";
import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import Home from "./Home";
import HomeAnnouncementBar from "./HomeAnnouncementBar";
import CartPage from "./Cart";
import OrderSuccessPage from "./OrderSuccess";
import { lazyRoute } from "./components/RouteLoadFallback";
import ErrorBoundary from "./components/ErrorBoundary";
import CartDrawer from "./components/CartDrawer";
import { CartProvider, useCart } from "./cart/CartContext";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { internalPathSearch, loginUrlWithReturn } from "./admin/adminUtils";
import { SiteSettingsProvider, useSiteSettings } from "./SiteSettingsContext";
import PostLoginWelcomeToast from "./auth/PostLoginWelcome";
import CartAddedToast from "./checkout/CartAddedToast";
import WishlistNoticeToast from "./wishlist/WishlistNoticeToast";
import { ShopMegaMenu } from "./components/ShopNavMenu";
import MobileNavDrawer from "./components/MobileNavDrawer";
import PwaInstallNavItem from "./components/PwaInstallNavItem";
import PwaInstallBanner from "./components/PwaInstallBanner";
import PwaUpdateBanner from "./components/PwaUpdateBanner";
import { PwaInstallProvider } from "./context/PwaInstallContext";
import LegacyUrlRedirect, {
  legacyRedirectRoutePaths,
} from "./LegacyUrlRedirect";
import { WishlistProvider, useWishlist } from "./wishlist/WishlistContext";
import ScrollToTop from "./ScrollToTop";
import Ga4Init from "./analytics/Ga4Init";
import ClarityInit from "./analytics/ClarityInit";
import StorefrontAttributionTracker from "./storefront/StorefrontAttributionTracker";
import StorefrontEngagementTracker from "./storefront/StorefrontEngagementTracker";
import StorefrontPageViewTracker from "./storefront/StorefrontPageViewTracker";
import StorefrontLivePulseTracker from "./storefront/StorefrontLivePulseTracker";
import FloatingSocialFab from "./components/FloatingSocialFab";
import { ConfirmDialogProvider } from "./components/ConfirmDialog";
import SiteFooter from "./components/footer/SiteFooter";
import BrandLogo from "./components/BrandLogo";
import {
  brandNameFromSettings,
  brandLogoSrcFromSettings,
  getBrandCopy,
  getFooterCopy,
} from "./siteBrand";
import { getMergedStoreCopy } from "./storeCopy";
import {
  customJewelryMainNavLabel,
  isCustomJewelryMainNavVisible,
} from "./jewelry/customJewelrySettings";
import { IconCart, IconHeart, IconUser } from "./components/icons/StorefrontIcons";
import {
  closeMobileNav,
  createSameRouteNavHandler,
  isSameNavRoute,
  isShopSectionPath,
  parseInternalNavTarget,
  registerMobileNavClose,
  scheduleScrollToPageTop,
} from "./navSameRouteClick";

let checkoutConfigPromise = null;

const Admin = lazyRoute(() => import("./Admin"), "Loading admin…");
const AdminBrandAssets = lazyRoute(
  () => import("./admin/AdminBrandAssets"),
  "Loading brand assets…",
);
const LoginPage = lazyRoute(() => import("./Login"), "Loading…");
const AccountPage = lazyRoute(() => import("./Account"), "Loading account…");
const CheckoutPage = lazyRoute(() => import("./Checkout"), "Loading checkout…");
const DynamicCategoryHub = lazyRoute(
  () => import("./DynamicCategoryHub"),
  "Loading collection…",
);
const DynamicCategoryList = lazyRoute(
  () => import("./DynamicCategoryList"),
  "Loading collection…",
);
const CustomJewelryPage = lazyRoute(
  () => import("./jewelry/CustomJewelryPage"),
  "Loading…",
);
const CustomJewelryGalleryPage = lazyRoute(
  () => import("./jewelry/CustomJewelryGalleryPage"),
  "Loading…",
);
const ProductSearch = lazyRoute(
  () => import("./ProductSearch"),
  "Loading search…",
);
const ProductDetailPage = lazyRoute(
  () => import("./ProductDetail"),
  "Loading product…",
);
const PolicyPage = lazyRoute(() => import("./PolicyPage"), "Loading…");
const NotFound = lazyRoute(() => import("./NotFound"), "Loading…");
const ForgotPasswordPage = lazyRoute(
  () => import("./ForgotPassword"),
  "Loading…",
);
const ResetPasswordPage = lazyRoute(
  () => import("./ResetPassword"),
  "Loading…",
);
const TrackOrderPage = lazyRoute(() => import("./TrackOrder"), "Loading…");
const OrderInvoicePage = lazyRoute(() => import("./OrderInvoice"), "Loading…");
const BlogPage = lazyRoute(() => import("./Blog"), "Loading…");
const BlogPostPage = lazyRoute(() => import("./BlogPost"), "Loading…");
const WishlistPage = lazyRoute(() => import("./WishlistPage"), "Loading…");
const GiftFinderRoute = lazyRoute(
  () => import("./giftFinder/GiftFinderRoute"),
  "Loading gift finder…",
);

function SiteHeaderAccountMenu({
  isAdmin,
  logout,
  pathname,
  onNavLinkClick,
  displayName = "",
  username = "",
  layout = "dropdown",
}) {
  const navigate = useNavigate();
  const menuActive = pathname === "/account" || pathname.startsWith("/admin");

  const initials = useMemo(() => {
    const name = (displayName || username || "").trim();
    if (!name) return "";
    const parts = name.split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  }, [displayName, username]);

  const handleAccountToggle = (e) => {
    if (typeof window !== "undefined" && window.bootstrap?.Dropdown) return;
    e.preventDefault();
    navigate("/account");
  };

  if (layout === "drawer") {
    return (
      <nav className="site-header-drawer-account" aria-label="Account">
        <NavLink
          className={({ isActive }) =>
            `site-header-drawer-account__link${isActive || pathname === "/account" ? " active" : ""}`
          }
          to="/account"
          onClick={onNavLinkClick("/account")}
        >
          My account
        </NavLink>
        {isAdmin ? (
          <NavLink
            className={({ isActive }) =>
              `site-header-drawer-account__link${isActive || pathname.startsWith("/admin") ? " active" : ""}`
            }
            to="/admin"
            onClick={onNavLinkClick("/admin")}
          >
            Catalog admin
          </NavLink>
        ) : null}
        <button
          type="button"
          className="site-header-drawer-account__link site-header-drawer-account__link--signout"
          onClick={() => {
            closeMobileNav();
            void logout();
          }}
        >
          Sign out
        </button>
      </nav>
    );
  }

  return (
    <div className="dropdown site-header-account-dropdown site-header-util-tile-wrap">
      <button
        type="button"
        className={`btn btn-sm dropdown-toggle site-header-account-toggle site-header-account-toggle--icon-only ${
          menuActive ? "btn-secondary" : "btn-outline-secondary"
        }`}
        data-bs-toggle="dropdown"
        aria-expanded="false"
        id="headerAccountMenu"
        aria-label="Account menu"
        title="Account"
        onClick={handleAccountToggle}
      >
        {initials ? (
          <span className="site-header-account-avatar" aria-hidden="true">{initials}</span>
        ) : (
          <IconUser className="site-header-account-toggle__icon site-header-util-tile__icon" />
        )}
        <span className="site-header-account-toggle__text d-lg-none">Account</span>
      </button>
      <ul
        className="dropdown-menu dropdown-menu-end shadow-sm"
        aria-labelledby="headerAccountMenu"
      >
        <li>
          <NavLink
            className="dropdown-item"
            to="/account"
            onClick={onNavLinkClick("/account")}
          >
            My account
          </NavLink>
        </li>
        {isAdmin ? (
          <li>
            <NavLink
              className="dropdown-item"
              to="/admin"
              onClick={onNavLinkClick("/admin")}
            >
              Catalog admin
            </NavLink>
          </li>
        ) : null}
        <li>
          <hr className="dropdown-divider" />
        </li>
        <li>
          <button
            type="button"
            className="dropdown-item"
            onClick={() => void logout()}
          >
            Sign out
          </button>
        </li>
      </ul>
    </div>
  );
}

function SiteHeaderWishlistLink({ onClick, bar = false }) {
  const { count } = useWishlist();
  return (
    <NavLink
      to="/wishlist"
      className={`site-header-icon-btn site-header-wishlist-btn${bar ? " site-header-icon-btn--bar" : " site-header-util-tile"}`}
      aria-label={count > 0 ? `Saved items, ${count}` : "Open saved items"}
      onClick={onClick}
    >
      <IconHeart className="site-header-util-tile__icon" />
      {!bar ? (
        <span className="site-header-util-tile__label d-lg-none">Wishlist</span>
      ) : null}
      {count > 0 ? (
        <span className="site-header-cart-badge" aria-hidden>
          {count > 99 ? "99+" : count}
        </span>
      ) : null}
    </NavLink>
  );
}

function SiteHeaderCartLink({ onClick, bar = false }) {
  const { itemCount, openDrawer } = useCart();
  return (
    <button
      type="button"
      className={`site-header-icon-btn site-header-cart-btn${bar ? " site-header-icon-btn--bar" : " site-header-util-tile border-0 bg-transparent"}`}
      aria-label={itemCount > 0 ? `Cart, ${itemCount} items` : "Open cart"}
      onClick={(e) => {
        openDrawer();
        onClick?.(e);
      }}
    >
      <IconCart className="site-header-util-tile__icon" />
      {!bar ? (
        <span className="site-header-util-tile__label d-lg-none">Bag</span>
      ) : null}
      {itemCount > 0 ? (
        <span className="site-header-cart-badge" aria-hidden>
          {itemCount > 99 ? "99+" : itemCount}
        </span>
      ) : null}
    </button>
  );
}

function SiteHeaderSignInLink({
  pathname,
  search,
  onNavLinkClick,
  className = "",
  iconOnly = false,
}) {
  const navigate = useNavigate();
  const loginActive = pathname === "/login" || pathname.startsWith("/login");
  const loginTo = loginUrlWithReturn(pathname, search);
  const loginSearch = internalPathSearch(loginTo);

  const handleClick = (e) => {
    onNavLinkClick("/login", { search: loginSearch })(e);
    if (e.defaultPrevented) return;
    navigate(loginTo);
  };

  return (
    <NavLink
      to={loginTo}
      title="Sign in"
      aria-label="Sign in to your account"
      onClick={handleClick}
      className={({ isActive }) =>
        `site-header-sign-in-link text-decoration-none${
          iconOnly
            ? " site-header-icon-btn site-header-sign-in-link--icon-only"
            : " site-header-util-tile"
        }${isActive || loginActive ? " site-header-sign-in-link--active" : ""}${
          className ? ` ${className}` : ""
        }`
      }
    >
      <IconUser className="site-header-sign-in-link__icon site-header-util-tile__icon" />
      {!iconOnly ? (
        <span className="site-header-sign-in-link__label site-header-account-toggle__text">
          Sign in
        </span>
      ) : null}
    </NavLink>
  );
}

function SiteAdminHeader({
  brandName,
  brandLogo,
  headerShowLogo,
  isAdmin,
  isAuthed,
  authReady,
  logout,
  pathname,
  search,
  onNavLinkClick,
  homeTo = "/admin",
  badgeLabel = "Store admin",
}) {
  return (
    <nav
      className="navbar navbar-light site-navbar site-navbar--admin"
      aria-label="Admin navigation"
    >
      <div className="container-fluid gx-3 gx-sm-4 align-items-center site-header-navbar-inner site-header-navbar-inner--admin">
        <Link
          className={`navbar-brand navbar-brand--wordmark d-flex align-items-center me-0${
            headerShowLogo ? " navbar-brand--with-logo" : ""
          }`}
          to={homeTo}
          onClick={onNavLinkClick(homeTo)}
        >
          {headerShowLogo ? (
            <BrandLogo
              src={brandLogo}
              alt=""
              className="navbar-logo me-2"
              size={38}
              aria-hidden
            />
          ) : null}
          <span className="brand-text">{brandName}</span>
        </Link>
        <span className="site-header-admin-badge">{badgeLabel}</span>
        {isAuthed && authReady ? (
          <nav className="site-header-admin-nav" aria-label="Admin areas">
            <NavLink
              to="/account"
              className={({ isActive }) =>
                `site-header-admin-nav__link${isActive || pathname === "/account" ? " site-header-admin-nav__link--active" : ""}`
              }
              onClick={onNavLinkClick("/account")}
            >
              My account
            </NavLink>
            {isAdmin ? (
              <NavLink
                to="/admin"
                className={({ isActive }) =>
                  `site-header-admin-nav__link${isActive || pathname.startsWith("/admin") ? " site-header-admin-nav__link--active" : ""}`
                }
                onClick={onNavLinkClick("/admin")}
              >
                Catalog admin
              </NavLink>
            ) : null}
          </nav>
        ) : null}
        <div className="site-header-admin-tools ms-auto d-flex align-items-center gap-2 flex-shrink-0">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-sm btn-outline-secondary site-header-admin-view-store"
          >
            View store
          </a>
          {isAuthed && authReady ? (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary site-header-admin-sign-out"
              onClick={() => void logout()}
            >
              Sign out
            </button>
          ) : (
            <SiteHeaderSignInLink
              pathname={pathname}
              search={search}
              onNavLinkClick={onNavLinkClick}
              iconOnly
            />
          )}
        </div>
      </div>
    </nav>
  );
}

function SiteHeaderSearch({
  headerSearchQ,
  setHeaderSearchQ,
  onSubmit,
  onAfterSubmit,
  variant = "both",
}) {
  const { settings: site } = useSiteSettings();
  const store = getMergedStoreCopy(site);
  const handleSubmit = (e) => {
    onSubmit(e);
    onAfterSubmit?.();
  };
  const showDesktop = variant === "both" || variant === "desktop";
  const showDrawer = variant === "both" || variant === "drawer";

  return (
    <>
      {showDesktop ? (
      <form
        className="site-header-search-form site-header-search-form--desktop d-none d-lg-block"
        onSubmit={handleSubmit}
        role="search"
        aria-label="Search products"
      >
        <div className="input-group site-header-search-group shadow-sm">
          <input
            type="search"
            className="form-control"
            placeholder={store.nav_search_placeholder}
            aria-label={store.nav_search_placeholder}
            value={headerSearchQ}
            onChange={(e) => setHeaderSearchQ(e.target.value)}
            autoComplete="off"
          />
          <button
            type="submit"
            className="btn btn-primary px-3"
            aria-label="Search"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              fill="currentColor"
              viewBox="0 0 16 16"
              aria-hidden
            >
              <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
            </svg>
          </button>
        </div>
      </form>
      ) : null}
      {showDrawer ? (
      <form
        className="site-header-search-form site-header-search-form--drawer d-lg-none"
        onSubmit={handleSubmit}
        role="search"
        aria-label="Search products"
      >
        <label
          className="site-header-search-drawer-label"
          htmlFor="mobile-menu-search"
        >
          Search the catalog
        </label>
        <div className="input-group site-header-search-group site-header-search-group--drawer shadow-sm">
          <span
            className="input-group-text site-header-search-drawer-icon"
            aria-hidden="true"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
            </svg>
          </span>
          <input
            id="mobile-menu-search"
            type="search"
            className="form-control"
            placeholder={store.nav_search_placeholder}
            aria-label={store.nav_search_placeholder}
            value={headerSearchQ}
            onChange={(e) => setHeaderSearchQ(e.target.value)}
            autoComplete="off"
          />
          <button
            type="submit"
            className="btn btn-primary site-header-search-drawer-submit"
            aria-label="Search"
          >
            <span className="site-header-search-drawer-submit__text">
              Search
            </span>
            <svg
              className="site-header-search-drawer-submit__icon"
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              fill="currentColor"
              viewBox="0 0 16 16"
              aria-hidden="true"
            >
              <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
            </svg>
          </button>
        </div>
      </form>
      ) : null}
    </>
  );
}

function AppInner() {
  const location = useLocation();
  const navigate = useNavigate();
  const { ready: authReady, isAuthed, isAdmin, isStaff, logout, displayName, username } = useAuth();
  const isCatalogAdminRoute = location.pathname.startsWith("/admin");
  const isStaffAccountRoute =
    authReady && isStaff && location.pathname.startsWith("/account");
  /** Phone / iPad (< lg): staff get admin header on every route (no shop category accordion). */
  const [isBelowLg, setIsBelowLg] = useState(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia("(max-width: 991.98px)").matches
      : false,
  );
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const mql = window.matchMedia("(max-width: 991.98px)");
    const onChange = (e) => setIsBelowLg(e.matches);
    setIsBelowLg(mql.matches);
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else mql.removeListener(onChange);
    };
  }, []);
  const isStaffMobileViewport = authReady && isStaff && isBelowLg;
  /** Swap to SiteAdminHeader (hides storefront shop categories on phone/iPad). */
  const useStaffHeader =
    isCatalogAdminRoute || isStaffAccountRoute || isStaffMobileViewport;
  /**
   * Admin workspace background + main padding contract.
   * Must NOT apply on storefront pages (even with staff header) — otherwise wine wash,
   * home luxury spacing, and session padding break.
   */
  const useAdminAppChrome = isCatalogAdminRoute || isStaffAccountRoute;
  const staffHeaderHomeTo =
    isCatalogAdminRoute || (isAdmin && !isStaffAccountRoute)
      ? "/admin"
      : "/account";
  const staffHeaderBadge = isCatalogAdminRoute
    ? "Store admin"
    : isAdmin
      ? "Admin workspace"
      : "Team account";
  const { settings: site } = useSiteSettings();
  const brandName = brandNameFromSettings(site);
  const brandLogo = brandLogoSrcFromSettings(site);
  const brandCopy = getBrandCopy(site.page_copy);
  /** Show logo + wordmark unless turned off in Admin → Site → Brand. */
  const headerShowLogo = brandCopy.header_show_logo !== false;
  const footerCopy = getFooterCopy(site.page_copy);
  const store = getMergedStoreCopy(site);
  const showCustomJewelryNav = isCustomJewelryMainNavVisible(site?.store_copy);
  const customJewelryNavLabel = customJewelryMainNavLabel(site?.store_copy);
  const [headerSearchQ, setHeaderSearchQ] = useState("");
  const [shippingRules, setShippingRules] = useState(null);
  // Dropdown state for Shop (desktop hover; tap-to-toggle on touch)
  const [shopOpen, setShopOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const closeMobileDrawer = useCallback(() => setMobileNavOpen(false), []);

  useEffect(() => registerMobileNavClose(closeMobileDrawer), [closeMobileDrawer]);

  useEffect(() => {
    if (!isBelowLg) setMobileNavOpen(false);
  }, [isBelowLg]);
  const [shopCloseTimeout, setShopCloseTimeout] = useState(null);
  const shopRef = useRef();

  useEffect(() => {
    let cancelled = false;
    if (!checkoutConfigPromise) {
      checkoutConfigPromise = fetch("/api/checkout_config.php")
        .then((r) => r.json())
        .catch(() => null);
    }
    checkoutConfigPromise
      .then((d) => {
        if (!cancelled && d?.ok && d.shipping_rules) {
          setShippingRules(d.shipping_rules);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  /** Re-tap current nav item: close mobile menu and scroll to top (no full reload). */
  const onNavLinkClick = createSameRouteNavHandler(location.pathname, {
    onBefore: closeMobileNav,
    currentSearch: location.search,
  });

  const shopPrefersHover = () =>
    typeof window !== "undefined" &&
    window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  const handleMouseEnter = () => {
    if (!shopPrefersHover()) return;
    if (shopCloseTimeout) {
      clearTimeout(shopCloseTimeout);
      setShopCloseTimeout(null);
    }
    setShopOpen(true);
  };

  const handleMouseLeave = () => {
    if (!shopPrefersHover()) return;
    const timeout = setTimeout(() => {
      setShopOpen(false);
    }, 300);
    setShopCloseTimeout(timeout);
  };

  const handleShopNavClick = (e) => {
    // Touch / coarse pointer: first tap opens mega-menu; second tap navigates to /shop.
    if (!shopPrefersHover()) {
      if (!shopOpen) {
        e.preventDefault();
        e.stopPropagation();
        if (shopCloseTimeout) {
          clearTimeout(shopCloseTimeout);
          setShopCloseTimeout(null);
        }
        setShopOpen(true);
        return;
      }
    }
    onNavLinkClick("/shop")(e);
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (shopRef.current && !shopRef.current.contains(event.target)) {
        setShopOpen(false);
        if (shopCloseTimeout) {
          clearTimeout(shopCloseTimeout);
          setShopCloseTimeout(null);
        }
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside, { passive: true });
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      if (shopCloseTimeout) {
        clearTimeout(shopCloseTimeout);
      }
    };
  }, [shopCloseTimeout]);

  const headerFixedRef = useRef(null);
  const headerMetricsRef = useRef(null);
  const [headerCompact, setHeaderCompact] = useState(false);
  useEffect(() => {
    const onScroll = () => setHeaderCompact(window.scrollY > 48);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const el = headerFixedRef.current;
    if (!el) return undefined;
    let rafId = 0;
    const syncHeaderHeight = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        const shell = el.querySelector(".site-header-shell");
        const fixedRect = el.getBoundingClientRect();
        let clearance = 72;
        if (shell) {
          const shellRect = shell.getBoundingClientRect();
          clearance = Math.ceil(shellRect.bottom - fixedRect.top);
        } else {
          clearance = Math.ceil(fixedRect.height);
        }
        clearance = Math.max(52, clearance);
        const blockHeight = Math.ceil(fixedRect.height);
        // Admin/account workspaces pad via --site-header-clearance; storefront uses full fixed height.
        const offsetPx = useAdminAppChrome ? clearance : blockHeight;
        const next = { clearance, blockHeight, offsetPx };
        const prev = headerMetricsRef.current;
        if (
          prev &&
          prev.clearance === next.clearance &&
          prev.blockHeight === next.blockHeight &&
          prev.offsetPx === next.offsetPx
        ) {
          return;
        }
        headerMetricsRef.current = next;
        document.documentElement.style.setProperty(
          "--site-header-clearance",
          `${clearance}px`,
        );
        document.documentElement.style.setProperty(
          "--site-header-height",
          `${blockHeight}px`,
        );
        document.documentElement.style.setProperty(
          "--site-header-offset",
          `${offsetPx}px`,
        );
      });
    };
    syncHeaderHeight();
    const ro = new ResizeObserver(syncHeaderHeight);
    ro.observe(el);
    window.addEventListener("resize", syncHeaderHeight);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      ro.disconnect();
      window.removeEventListener("resize", syncHeaderHeight);
    };
  }, [authReady, isAuthed, isStaff, headerCompact, location.pathname, isCatalogAdminRoute, useStaffHeader, useAdminAppChrome, site?.announcement_enabled, site?.announcement_text]);

  useEffect(() => {
    const nav = document.getElementById("navbarNav");
    if (!nav) return undefined;
    const resync = () => {
      window.requestAnimationFrame(() => {
        window.dispatchEvent(new Event("resize"));
      });
    };
    nav.addEventListener("shown.bs.collapse", resync);
    nav.addEventListener("hidden.bs.collapse", resync);
    return () => {
      nav.removeEventListener("shown.bs.collapse", resync);
      nav.removeEventListener("hidden.bs.collapse", resync);
    };
  }, []);

  useEffect(() => {
    setShopOpen(false);
    setMobileNavOpen(false);
    closeMobileNav();
  }, [location.pathname]);

  /** Release body scroll lock if a portaled modal unmounted without cleanup.
   *  Skip while any fullscreen overlay / cart drawer still holds the lock. */
  useEffect(() => {
    const overlayStillOpen =
      document.body.classList.contains("cart-drawer-open") ||
      document.body.classList.contains("mobile-nav-open") ||
      document.body.style.position === "fixed" ||
      Boolean(
        document.querySelector(
          ".image-modal-backdrop, .admin-ops-modal-overlay, .cj-request-modal, [data-fullscreen-overlay], .mobile-nav-drawer"
        )
      );
    if (overlayStillOpen) return;
    document.body.style.overflow = "";
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.width = "";
  }, [location.pathname]);

  /** Same-route tap: scroll to top + close mobile menu (capture phase — works for all internal links). */
  useEffect(() => {
    const onDocClick = (e) => {
      const anchor = e.target.closest("a[href]");
      if (!anchor || anchor.target === "_blank") return;
      const raw = anchor.getAttribute("href") || "";
      if (
        !raw ||
        raw.startsWith("#") ||
        /^https?:/i.test(raw) ||
        raw.startsWith("mailto:") ||
        raw.startsWith("tel:")
      ) {
        return;
      }
      let path = raw;
      let linkSearch = "";
      try {
        const url = new URL(raw, window.location.origin);
        if (url.origin !== window.location.origin) return;
        path = url.pathname;
        linkSearch = url.search;
      } catch {
        const parsed = parseInternalNavTarget(raw);
        path = parsed.pathname;
        linkSearch = parsed.search;
      }
      const sameRoute = isSameNavRoute(location.pathname, path, {
        end: path === "/",
        search: linkSearch,
        currentSearch: location.search,
      });
      if (!sameRoute) return;
      e.preventDefault();
      closeMobileNav();
      scheduleScrollToPageTop({ smooth: true });
    };
    document.addEventListener("click", onDocClick, true);
    return () => document.removeEventListener("click", onDocClick, true);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (location.pathname !== "/search") return;
    const p = new URLSearchParams(location.search).get("q") || "";
    setHeaderSearchQ(p);
  }, [location.pathname, location.search]);

  const footerLoginTo = loginUrlWithReturn(location.pathname, location.search);
  const footerLoginSearch = internalPathSearch(footerLoginTo);

  return (
    <PwaInstallProvider>
      <>
        <ScrollToTop />
        <Ga4Init />
        <ClarityInit />
        <StorefrontAttributionTracker />
        <StorefrontEngagementTracker />
        <StorefrontPageViewTracker />
        <StorefrontLivePulseTracker />
        <a href="#main-content" className="skip-to-main">
          {store.skip_to_main}
        </a>
        <div
          className={`app-bg${authReady && isAuthed ? " app-bg--session" : " app-bg--guest"}${useAdminAppChrome ? " app-bg--admin-route" : ""}${useStaffHeader && !useAdminAppChrome ? " app-bg--staff-mobile-header" : ""}`}
        >
          <div className="site-header-fixed" ref={headerFixedRef}>
            {!useStaffHeader ? <HomeAnnouncementBar /> : null}
            <header className="site-header-nav">
              <div
                className={`site-header-shell site-header-shell--storefront${
                  headerCompact ? " site-header-shell--compact" : ""
                }${
                  authReady && isAuthed
                    ? " site-header-shell--authed"
                    : " site-header-shell--guest"
                }${useStaffHeader ? " site-header-shell--admin-route" : ""}${
                  isCatalogAdminRoute ? " site-header-shell--catalog-admin" : ""
                }${
                  isStaffAccountRoute ? " site-header-shell--staff-account" : ""
                }${
                  showCustomJewelryNav ? " site-header-shell--has-bespoke-nav" : ""
                }`}
              >
                {useStaffHeader ? (
                  <SiteAdminHeader
                    brandName={brandName}
                    brandLogo={brandLogo}
                    headerShowLogo={headerShowLogo}
                    isAdmin={isAdmin}
                    isAuthed={isAuthed}
                    authReady={authReady}
                    logout={logout}
                    pathname={location.pathname}
                    search={location.search}
                    onNavLinkClick={onNavLinkClick}
                    homeTo={staffHeaderHomeTo}
                    badgeLabel={staffHeaderBadge}
                  />
                ) : (
                <nav
                  className="navbar navbar-expand-lg navbar-light site-navbar"
                  aria-label="Main navigation"
                >
                  <div className="container-fluid gx-3 gx-sm-4 align-items-center site-header-navbar-inner">
                    <Link
                      className={`navbar-brand navbar-brand--wordmark d-flex align-items-center me-0 me-lg-2${
                        headerShowLogo ? " navbar-brand--with-logo" : ""
                      }`}
                      to="/"
                      onClick={onNavLinkClick("/", { end: true })}
                    >
                      {headerShowLogo ? (
                        <BrandLogo
                          src={brandLogo}
                          alt=""
                          className="navbar-logo me-2"
                          size={38}
                          aria-hidden
                        />
                      ) : null}
                      <span className="brand-text">{brandName}</span>
                    </Link>
                    <div className="site-header-mobile-tools d-flex d-lg-none align-items-center ms-auto flex-shrink-0">
                      {!isStaff ? (
                        <>
                          <SiteHeaderWishlistLink
                            bar
                            onClick={onNavLinkClick("/wishlist")}
                          />
                          <SiteHeaderCartLink
                            bar
                            onClick={onNavLinkClick("/cart")}
                          />
                        </>
                      ) : null}
                      <button
                        className="navbar-toggler border-0 site-header-mobile-tools__menu"
                        type="button"
                        aria-controls="mobile-nav-drawer"
                        aria-expanded={mobileNavOpen}
                        aria-label={mobileNavOpen ? "Close navigation" : "Open navigation"}
                        onClick={() => setMobileNavOpen((open) => !open)}
                      >
                        <span className="navbar-toggler-icon" />
                      </button>
                    </div>
                    <div
                      className="collapse navbar-collapse site-header-navbar-collapse"
                      id="navbarNav"
                    >
                      <ul
                        className={`navbar-nav site-navbar-nav mx-lg-0 justify-content-lg-center mb-2 mb-lg-0 align-items-lg-center text-center text-lg-start ${
                          authReady && isAuthed ? "py-0" : "py-2 py-lg-0"
                        }`}
                      >
                        <li className="nav-item">
                          <NavLink
                            className="nav-link"
                            to="/"
                            end
                            onClick={onNavLinkClick("/", { end: true })}
                          >
                            {store.nav_home}
                          </NavLink>
                        </li>
                        <li
                          className="nav-item dropdown d-none d-lg-block"
                          ref={shopRef}
                          onMouseEnter={handleMouseEnter}
                          onMouseLeave={handleMouseLeave}
                        >
                          <NavLink
                            to="/shop"
                            className={({ isActive }) =>
                              `nav-link dropdown-toggle${isActive ? " active" : ""}`
                            }
                            id="shopDropdown"
                            role="button"
                            aria-expanded={shopOpen}
                            aria-haspopup="true"
                            onClick={handleShopNavClick}
                          >
                            {store.nav_shop}
                          </NavLink>
                          <ul
                            className={`dropdown-menu shop-mega-menu-dropdown${shopOpen ? " show" : ""}`}
                            aria-labelledby="shopDropdown"
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                          >
                            <ShopMegaMenu
                              onNavigate={() => setShopOpen(false)}
                              store={store}
                            />
                          </ul>
                        </li>
                        <li className="nav-item">
                          <NavLink
                            className="nav-link"
                            to="/about"
                            onClick={onNavLinkClick("/about")}
                          >
                            {store.nav_about}
                          </NavLink>
                        </li>
                        {showCustomJewelryNav ? (
                          <li className="nav-item">
                            <NavLink
                              className="nav-link"
                              to="/custom-jewelry"
                              onClick={onNavLinkClick("/custom-jewelry")}
                            >
                              {customJewelryNavLabel}
                            </NavLink>
                          </li>
                        ) : null}
                        <li className="nav-item">
                          <NavLink
                            className="nav-link"
                            to="/exhibitions"
                            onClick={onNavLinkClick("/exhibitions")}
                          >
                            {store.nav_exhibitions}
                          </NavLink>
                        </li>
                        <li className="nav-item">
                          <NavLink
                            className="nav-link"
                            to="/contact"
                            onClick={onNavLinkClick("/contact")}
                          >
                            {store.nav_contact}
                          </NavLink>
                        </li>
                      </ul>
                      <div className="site-header-toolbar d-flex flex-wrap align-items-center justify-content-center justify-content-lg-end gap-2 gap-lg-2 w-100 w-lg-auto">
                        <SiteHeaderSearch
                          variant="desktop"
                          headerSearchQ={headerSearchQ}
                          setHeaderSearchQ={setHeaderSearchQ}
                          onAfterSubmit={closeMobileNav}
                          onSubmit={(e) => {
                            e.preventDefault();
                            const q = headerSearchQ.trim();
                            if (q)
                              navigate(`/search?q=${encodeURIComponent(q)}`);
                          }}
                        />
                        <div className="site-header-toolbar-actions d-none d-lg-flex align-items-center gap-1 gap-lg-2 flex-shrink-0">
                          <div
                            className="site-header-toolbar-divider d-none d-lg-block"
                            aria-hidden="true"
                          />
                          <div className="site-header-toolbar-actions__desktop-utils d-flex align-items-center gap-1 gap-lg-2">
                            <SiteHeaderWishlistLink
                              onClick={onNavLinkClick("/wishlist")}
                            />
                            {!isStaff ? (
                              <SiteHeaderCartLink
                                onClick={onNavLinkClick("/cart")}
                              />
                            ) : null}
                          </div>
                          {isAuthed && authReady ? (
                            <SiteHeaderAccountMenu
                              isAdmin={isAdmin}
                              logout={logout}
                              pathname={location.pathname}
                              onNavLinkClick={onNavLinkClick}
                              displayName={displayName}
                              username={username}
                            />
                          ) : (
                            <SiteHeaderSignInLink
                              pathname={location.pathname}
                              search={location.search}
                              onNavLinkClick={onNavLinkClick}
                              iconOnly
                            />
                          )}
                        </div>
                      </div>
                    </div>
                    {!isStaff ? (
                      <MobileNavDrawer
                        open={mobileNavOpen}
                        onClose={closeMobileDrawer}
                        store={store}
                        onNavLinkClick={onNavLinkClick}
                        showCustomJewelryNav={showCustomJewelryNav}
                        customJewelryNavLabel={customJewelryNavLabel}
                        shopSectionActive={isShopSectionPath(location.pathname)}
                        searchSlot={
                          <SiteHeaderSearch
                            variant="drawer"
                            headerSearchQ={headerSearchQ}
                            setHeaderSearchQ={setHeaderSearchQ}
                            onAfterSubmit={closeMobileDrawer}
                            onSubmit={(e) => {
                              e.preventDefault();
                              const q = headerSearchQ.trim();
                              if (q)
                                navigate(`/search?q=${encodeURIComponent(q)}`);
                            }}
                          />
                        }
                        accountSlot={
                          <div className="site-header-drawer-panel">
                            <PwaInstallNavItem onAfterClick={closeMobileDrawer} />
                            <div className="site-header-drawer-panel__footer">
                              {isAuthed && authReady ? (
                                <SiteHeaderAccountMenu
                                  layout="drawer"
                                  isAdmin={isAdmin}
                                  logout={logout}
                                  pathname={location.pathname}
                                  onNavLinkClick={onNavLinkClick}
                                />
                              ) : (
                                <SiteHeaderSignInLink
                                  pathname={location.pathname}
                                  search={location.search}
                                  onNavLinkClick={onNavLinkClick}
                                  className="site-header-drawer-sign-in"
                                />
                              )}
                            </div>
                          </div>
                        }
                      />
                    ) : null}
                  </div>
                </nav>
                )}
              </div>
            </header>
          </div>

          <PostLoginWelcomeToast />
          {!isStaff ? <CartAddedToast /> : null}
          {!isStaff ? <WishlistNoticeToast /> : null}
          {!isStaff ? <CartDrawer shippingRules={shippingRules} /> : null}
          {!useStaffHeader ? (
            <>
              <PwaInstallBanner />
              <PwaUpdateBanner />
              <FloatingSocialFab />
            </>
          ) : null}

          {/* Main Content */}
          <main
            id="main-content"
            tabIndex={-1}
            className={`main-content store-scope${isCatalogAdminRoute ? " main-content--admin" : ""}${isStaffAccountRoute ? " main-content--staff-account" : ""}`}
          >
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/exhibitions" element={<ExhibitionsPage />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/track-order" element={<TrackOrderPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route
                path="/account/invoice/:orderNumber"
                element={<OrderInvoicePage />}
              />
              <Route path="/policies/:slug" element={<PolicyPage />} />
              <Route path="/search" element={<ProductSearch />} />
              <Route path="/gift-finder" element={<GiftFinderRoute />} />
              <Route
                path="/product/:categorySlug/:productId"
                element={<ProductDetailPage />}
              />
              <Route path="/custom-jewelry/designs" element={<CustomJewelryGalleryPage />} />
              <Route path="/custom-jewelry" element={<CustomJewelryPage />} />
              <Route path="/shop" element={<Shop />} />
              {legacyRedirectRoutePaths().map((path) => (
                <Route key={path} path={path} element={<LegacyUrlRedirect />} />
              ))}
              <Route
                path="/shop/:categorySlug/:groupKey"
                element={<DynamicCategoryList />}
              />
              <Route
                path="/shop/:categorySlug"
                element={<DynamicCategoryHub />}
              />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/wishlist" element={<WishlistPage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route
                path="/order-success/:orderNumber"
                element={<OrderSuccessPage />}
              />
              <Route path="/account" element={<AccountPage />} />
              <Route path="/admin/brand" element={<AdminBrandAssets />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/brand" element={<Navigate to="/admin/brand" replace />} />
              <Route path="/404" element={<NotFound />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>

          <SiteFooter
            site={site}
            brandName={brandName}
            brandLogo={brandLogo}
            footerCopy={footerCopy}
            store={store}
            onNavLinkClick={onNavLinkClick}
            authReady={authReady}
            isAuthed={isAuthed}
            isAdmin={isAdmin}
            footerLoginTo={footerLoginTo}
            footerLoginSearch={footerLoginSearch}
          />
        </div>
      </>
    </PwaInstallProvider>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <Router>
          <AuthProvider>
            <CartProvider>
              <WishlistProvider>
                <SiteSettingsProvider>
                  <ConfirmDialogProvider>
                    <AppInner />
                  </ConfirmDialogProvider>
                </SiteSettingsProvider>
              </WishlistProvider>
            </CartProvider>
          </AuthProvider>
        </Router>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
